import { useExternalParams } from 'shared/hooks/use-external-params';
import { useMemo } from 'react';
import { ProjectPlanningPageParams } from './use-project-planning.types';
import { FilledStatus } from '../../../../../../../shared/components/filters/inputs/filled-status/filter-filled-status.types';
import { stringifyFilters } from '../../../../../../../shared/utils/stringify-filters';

const initialValues = {
  projectDetailId: {
    initialValue: undefined,
    type: 'string',
  },
  toDate: {
    initialValue: undefined,
    type: 'date',
  },
  company: {
    initialValue: undefined,
    type: 'string',
  },
  branchOffices: {
    initialValue: undefined,
    type: 'array',
  },
  departments: {
    initialValue: undefined,
    type: 'array',
  },
  filledStatus: {
    initialValue: undefined,
    type: 'string',
  },
  jobType: {
    initialValue: undefined,
    type: 'array',
  },
  isFreelance: {
    initialValue: undefined,
    type: 'boolean',
  },
  isTempWork: {
    initialValue: undefined,
    type: 'boolean',
  },
  areFreelanceApplications: {
    initialValue: undefined,
    type: 'boolean',
  },
  areTempworkApplications: {
    initialValue: undefined,
    type: 'boolean',
  },
  areNoApplications: {
    initialValue: undefined,
    type: 'boolean',
  },
} as const;

const useProjectPlanningPage = () => {
  const [params, setParams] = useExternalParams(initialValues);

  const projectPlanningPageParams: ProjectPlanningPageParams = useMemo(() => {
    return {
      toDate: params.toDate || undefined,
      company: params.company || undefined,
      branchOffices: params.branchOffices as string[] | undefined,
      departments: params.departments as string[] | undefined,
      filledStatus: (params.filledStatus as FilledStatus) || undefined,
      jobType: params.jobType as string[] | undefined,
      projectDetailId: params.projectDetailId || undefined,
      isFreelance: params.isFreelance ?? undefined,
      isTempWork: params.isTempWork ?? undefined,
      areFreelanceApplications: params.areFreelanceApplications ?? undefined,
      areTempworkApplications: params.areTempworkApplications ?? undefined,
      areNoApplications: params.areNoApplications ?? undefined,
    };
  }, [params]);

  const selectedFilterCount = getActiveFiltersCount(projectPlanningPageParams);

  const strigifiedFilters = useMemo(
    () => stringifyFilters(projectPlanningPageParams),
    [projectPlanningPageParams]
  );

  return {
    params: projectPlanningPageParams,
    setParams,
    selectedFilterCount,
    strigifiedFilters: strigifiedFilters,
  };
};

const getActiveFiltersCount = (filters: ProjectPlanningPageParams) => {
  const {
    isFreelance,
    isTempWork,
    areFreelanceApplications,
    areTempworkApplications,
    areNoApplications,
    ...filtersWithoutDuplicatedCounts
  } = filters;

  const filterLengthWithoutEmployment = Object.values(filtersWithoutDuplicatedCounts).filter(
    (value) => {
      return value !== null && value !== undefined && value !== '';
    }
  ).length;

  const hasEmploymentFilter = isFreelance || isTempWork;
  if (hasEmploymentFilter) {
    return filterLengthWithoutEmployment + 1;
  }

  const hasApplicationFilter =
    areFreelanceApplications || areTempworkApplications || areNoApplications;
  if (hasApplicationFilter) {
    return filterLengthWithoutEmployment + 1;
  }

  return filterLengthWithoutEmployment;
};

export { useProjectPlanningPage };
